
import React from 'react';
import Layout from '@/components/layout/Layout';
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { Video, Globe, Clock, Music } from 'lucide-react';

const OnlineLocation = () => {
  const features = [
    {
      title: "Fra hele verden",
      description: "Modtag undervisning hjemmefra, uanset hvor du befinder dig i Danmark eller i udlandet.",
      icon: "location"
    },
    {
      title: "Fleksible tidspunkter",
      description: "Book timer når det passer dig, uden transporttid - perfekt til travle hverdage.",
      icon: "clock"
    },
    {
      title: "Alle niveauer",
      description: "Online undervisning for både begyndere og øvede - tilpasset dine musikalske behov.",
      icon: "music"
    },
    {
      title: "Professionel kvalitet",
      description: "Samme høje undervisningskvalitet som ved fysisk fremmøde, med personlig feedback.",
      icon: "award"
    }
  ];

  const testimonials = [
    {
      name: "Michael Larsen",
      location: "Aarhus",
      text: "Jeg troede ikke, online klaverundervisning kunne være så effektivt, men Oscar formår virkelig at give den samme kvalitet, som hvis jeg sad ved siden af ham.",
      stars: 5
    },
    {
      name: "Emilie Rasmussen",
      location: "Odense",
      text: "Det fungerer fantastisk at have timerne online. Jeg sparer transporttid, og Oscar er lige så opmærksom og hjælpsom, som hvis vi sad i samme rum.",
      stars: 5
    },
    {
      name: "Henrik Jørgensen",
      location: "Aalborg",
      text: "Som travl forretningsmand var online timer den perfekte løsning for mig. Oscar er utrolig dygtig til at tilpasse undervisningen til online formatet.",
      stars: 5
    }
  ];

  return (
    <Layout>
      <LocationHero 
        title="Online Klaverundervisning"
        description="Professionel klaverundervisning online med Oscar Gilbert - lær at spille klaver i dit eget tempo og i trygge rammer hjemmefra."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Online Klaverundervisning med <span className="text-piano-gold">Oscar Gilbert</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Med online klaverundervisning kan du lære at spille klaver hjemmefra, uanset hvor i landet eller verden du befinder dig. Oscar Gilbert tilbyder professionel undervisning via video, der giver dig samme høje kvalitet som ved fysisk fremmøde.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Online undervisning fungerer overraskende godt til klaver. Med de rette kameravinkler kan Oscar se både dit klaviatur og dine hænder, hvilket giver mulighed for præcis vejledning i teknik og kropsholdning.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Alt du behøver er et klaver eller keyboard, en computer, tablet eller smartphone med kamera og en stabil internetforbindelse.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Book Gratis Online Prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/undervisning">Se Undervisningstilbud</Link>
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative w-full max-w-lg">
                <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-500 rounded-lg blur-lg opacity-25"></div>
                <div className="relative bg-white p-6 rounded-lg shadow-xl">
                  <div className="bg-gray-100 rounded-lg p-8 space-y-6">
                    <h3 className="text-2xl font-serif font-bold mb-4 text-center">Sådan foregår online undervisning</h3>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-piano-gold text-white">
                          <Video className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium">Video platform</h4>
                        <p className="text-gray-600">Zoom, Skype eller FaceTime efter dit valg</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-piano-gold text-white">
                          <Globe className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium">Fra hvor som helst</h4>
                        <p className="text-gray-600">Ingen geografiske begrænsninger</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-piano-gold text-white">
                          <Clock className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium">Fleksible tider</h4>
                        <p className="text-gray-600">Tider der passer til din hverdag</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-piano-gold text-white">
                          <Music className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium">Samme høje kvalitet</h4>
                        <p className="text-gray-600">Personlig feedback og veldokumenterede fremskridt</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="online undervisning" />
      
      <LocationCTA locationName="online" />
    </Layout>
  );
};

export default OnlineLocation;
